import { useRef, useState, type ReactNode } from 'react';
import { CalendarBlankIcon } from '@phosphor-icons/react';
import { formatClockTime, formatDayMonth, parseLocalDateTime } from '@voll-intelligence/dates';
import {
  DateTimePicker,
  FIELD_TRIGGER_EMPTY_CLASS,
  FIELD_TRIGGER_GLYPH_CLASS,
  FieldTrigger,
  wantedAttr,
  type DateTimeFieldCopy,
} from '@voll-intelligence/intelligence-ui-kit';
import type {
  MobilityPlannerBlock,
  MobilityPlannerSearch,
} from '@voll-intelligence/genui-contract';
import { useGenUiI18n, type GenUiTranslate } from '../../../shared/ui/i18n.js';
import { PlannerColumn } from './planner_parts.js';

type When = NonNullable<MobilityPlannerSearch['when']>;

/** O valor do campo, `YYYY-MM-DDTHH:mm`; enquanto falta um pedaço, só o que se sabe. */
const MOMENT = /^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2})$/;

/** O Quando como o agente o deixou: o dia e a hora já entendidos, em branco o que falta. */
export function initialWhen(ui: MobilityPlannerBlock): string {
  const { date, time } = ui.editableWhen ?? {};
  return date || time ? `${date ?? ''}T${time ?? ''}` : '';
}

/** A data e a hora da busca: nada sem o Quando editável, `null` enquanto falta uma das duas. */
export function searchWhen(ui: MobilityPlannerBlock, value: string): When | null | undefined {
  if (!ui.editableWhen) return undefined;
  const [, date, time] = MOMENT.exec(value) ?? [];
  return date && time ? { date, time } : null;
}

/** "15 out, 08:30" — o ano só fora do ano corrente. */
function momentLabel(value: string, locale: string): string {
  const at = parseLocalDateTime(value);
  return at ? `${formatDayMonth(at, locale, new Date())}, ${formatClockTime(at, locale)}` : value;
}

/** O Quando na pílula e no registro: o escolhido no card, ou o texto do agente. */
export function whenText(
  ui: MobilityPlannerBlock,
  value: string,
  locale: string,
): string | undefined {
  return MOMENT.test(value) ? momentLabel(value, locale) : ui.when;
}

function whenCopy(t: GenUiTranslate, locale: string, title: string): DateTimeFieldCopy {
  return {
    title,
    placeholder: t('mobilityPlanner.whenPending'),
    confirm: t('mobilityPlanner.whenConfirm'),
    back: t('mobilityPlanner.whenBack'),
    previousMonth: t('mobilityPlanner.previousMonth'),
    nextMonth: t('mobilityPlanner.nextMonth'),
    hour: t('mobilityPlanner.hour'),
    minute: t('mobilityPlanner.minute'),
    format: (value) => momentLabel(value, locale),
    summary: (value) => momentLabel(value, locale),
  };
}

/** O Para quando que o card deixa escolher: só o Confirmar grava; `wanted` quando só ele falta. */
function WhenField({
  ui,
  value,
  wanted,
  onChange,
}: Readonly<{
  ui: MobilityPlannerBlock;
  value: string;
  wanted: boolean;
  onChange: (value: string) => void;
}>): ReactNode {
  const { t, locale } = useGenUiI18n();
  const label = t('mobilityPlanner.when');
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const text = whenText(ui, value, locale);
  const shown = text ?? t('mobilityPlanner.whenPending');
  return (
    <PlannerColumn label={label} wide={false}>
      <FieldTrigger
        ref={triggerRef}
        fill
        aria-haspopup="dialog"
        aria-expanded={open}
        // O rótulo da coluna é visual: o nome junta a pergunta e a resposta.
        aria-label={`${label}: ${shown}`}
        {...(open ? { 'data-popup-open': '' } : {})}
        {...wantedAttr(wanted)}
        onClick={() => {
          setOpen(true);
        }}
      >
        <CalendarBlankIcon className={FIELD_TRIGGER_GLYPH_CLASS} aria-hidden="true" />
        <span className={`truncate${text ? '' : ` ${FIELD_TRIGGER_EMPTY_CLASS}`}`}>{shown}</span>
      </FieldTrigger>
      <DateTimePicker
        open={open}
        onClose={() => {
          setOpen(false);
        }}
        triggerRef={triggerRef}
        value={value}
        copy={whenCopy(t, locale, label)}
        locale={locale}
        timeline="future"
        onConfirm={onChange}
      />
    </PlannerColumn>
  );
}

/** O que falta do Para quando, para o Buscar dizer: as duas partes, só a data ou só a hora. */
export interface WhenMissingText {
  both: string;
  date: string;
  time: string;
}

function missingPart(value: string, text: WhenMissingText): string {
  const [date = '', time = ''] = value.split('T');
  if (date && !time) return text.time;
  if (time && !date) return text.date;
  return text.both;
}

/** O Para quando da fileira viva e, com os lugares prontos, o que o Buscar diz que falta. */
export function liveWhen({
  ui,
  value,
  placesReady,
  missingText,
  onChange,
}: Readonly<{
  ui: MobilityPlannerBlock;
  value: string;
  placesReady: boolean;
  missingText: WhenMissingText;
  onChange: (value: string) => void;
}>): { field: ReactNode; missing: string | undefined } {
  const missing = placesReady && searchWhen(ui, value) === null;
  return {
    field: ui.editableWhen ? (
      <WhenField ui={ui} value={value} wanted={missing} onChange={onChange} />
    ) : null,
    missing: missing ? missingPart(value, missingText) : undefined,
  };
}
